import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Package, FilePdf, FileImage, Warning, PencilSimple, Trash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { ProcurementTrackingCard } from '../components/ProcurementTrackingCard';

const API_URL = '';

function getAuthFileUrl(storagePath) {
  // httpOnly cookies are sent automatically for same-origin requests (window.open, img src)
  return `${API_URL}/api/files/${storagePath}`;
}

const STATUS_MAP = {
  pending_procurement: { label: 'Pending Procurement', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  procurement_approved: { label: 'Procurement Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  procurement_rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
  assigned_to_production: { label: 'Assigned to Production', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  under_production: { label: 'Under Production', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  production_on_hold: { label: 'Production On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  production_completed: { label: 'Production Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled_for_dispatch: { label: 'Scheduled for Dispatch', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  ready_to_dispatch: { label: 'Ready to Dispatch', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  dispatched: { label: 'Dispatched', className: 'bg-zinc-50 text-zinc-700 border-zinc-200' },
  pending_for_installation: { label: 'Pending Installation', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  installation_completed: { label: 'Installation Completed', className: 'bg-teal-50 text-teal-700 border-teal-200' },
  revoked_by_admin: { label: 'Revoked by Admin', className: 'bg-rose-100 text-rose-800 border-rose-300' }
};

function StatusBadge({ status }) {
  const config = STATUS_MAP[status] || { label: status, className: 'bg-zinc-50 text-zinc-700' };
  return <Badge className={`${config.className} border`}>{config.label}</Badge>;
}

function FileCard({ file, index, onView }) {
  const isPdf = file.content_type === 'application/pdf';
  const isImage = file.content_type?.startsWith('image/');
  const hasStoragePath = !!file.storage_path;
  const thumbnailUrl = hasStoragePath
    ? getAuthFileUrl(file.storage_path)
    : (file.data?.startsWith?.('data:') ? file.data : `data:${file.content_type};base64,${file.data}`);

  return (
    <div
      data-testid={`file-${index}`}
      onClick={() => onView(file)}
      className="border border-zinc-300 rounded-sm p-4 hover:bg-zinc-50 cursor-pointer transition-colors"
    >
      {isImage && hasStoragePath ? (
        <img src={thumbnailUrl} alt={file.filename} className="w-full h-24 object-cover rounded-sm mb-2" />
      ) : (
        <div className="flex items-center gap-3">
          {isPdf ? (
            <FilePdf size={32} weight="bold" className="text-red-600" />
          ) : (
            <FileImage size={32} weight="bold" className="text-blue-600" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-zinc-950 truncate">{file.filename}</p>
            <p className="text-xs text-zinc-500 capitalize">{file.file_type}</p>
          </div>
        </div>
      )}
      {isImage && hasStoragePath && (
        <p className="text-sm font-bold text-zinc-950 truncate mt-1">{file.filename}</p>
      )}
    </div>
  );
}

function DetailField({ label, children, className = '' }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className={`text-zinc-950 mt-1 ${className}`}>{children}</p>
    </div>
  );
}

function JobInfoCard({ jobOrder }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Job Order Information</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <DetailField label="JO Number" className="text-lg font-bold">{jobOrder.jo_number}</DetailField>
          <DetailField label="JO Date">{formatDate(jobOrder.jo_date) || jobOrder.jo_date}</DetailField>
          <DetailField label="Quotation No">{jobOrder.quotation_no}</DetailField>
          <DetailField label="Executive Name">{jobOrder.executive_name}</DetailField>
          <DetailField label="Delivery Date" className="text-lg font-bold text-red-600">{formatDate(jobOrder.delivery_date || jobOrder.confirmed_delivery_date) || jobOrder.delivery_date || jobOrder.confirmed_delivery_date}</DetailField>
          {jobOrder.original_delivery_date && jobOrder.original_delivery_date !== (jobOrder.delivery_date || jobOrder.confirmed_delivery_date) && (
            <DetailField label="Original Delivery Date (Reference)" className="text-zinc-500" data-testid="original-delivery-date">{formatDate(jobOrder.original_delivery_date) || jobOrder.original_delivery_date}</DetailField>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerCard({ jobOrder }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Customer Details</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <DetailField label="Customer Name" className="text-lg font-bold">{jobOrder.customer_name}</DetailField>
          <DetailField label="Mobile Number">{jobOrder.customer_mobile}</DetailField>
          <DetailField label="Work Location">{jobOrder.work_location}</DetailField>
        </div>
      </CardContent>
    </Card>
  );
}

function SpecificationsCard({ jobOrder }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Window Specifications</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <DetailField label="Total Sqft" className="text-2xl font-light">{jobOrder.total_sqft}</DetailField>
          <DetailField label="No of Windows" className="text-2xl font-light">{jobOrder.no_of_windows}</DetailField>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Profile Type</p>
            <p className={`text-zinc-950 mt-1 text-lg ${jobOrder.profile_type === 'Colour' ? 'font-bold text-orange-600' : ''}`}>{jobOrder.profile_type}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Glass Type</p>
            <p className={`text-zinc-950 mt-1 text-lg ${jobOrder.glass_type !== 'Normal' ? 'font-bold text-blue-600' : ''}`}>{jobOrder.glass_type}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DispatchInfoCard({ jobOrder }) {
  if (!jobOrder.dispatch_schedule_date && !jobOrder.dispatched_at) return null;
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Dispatch Information</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {jobOrder.dispatch_schedule_date && (
            <DetailField label="Scheduled Dispatch Date" className="font-bold">{formatDate(jobOrder.dispatch_schedule_date) || jobOrder.dispatch_schedule_date}</DetailField>
          )}
          {jobOrder.dispatched_at && (
            <DetailField label="Dispatched On" className="font-bold">{formatDate(jobOrder.dispatched_at) || jobOrder.dispatched_at}</DetailField>
          )}
          {jobOrder.dispatch_remarks && (
            <DetailField label="Dispatch Remarks">{jobOrder.dispatch_remarks}</DetailField>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const ADVANCED_STATUSES = ['under_production', 'production_on_hold', 'production_completed', 'ready_to_dispatch', 'scheduled_for_dispatch', 'dispatched'];
const PRODUCTION_ACTIVE_STATUSES = ['assigned_to_production', 'under_production', 'production_on_hold', 'pending_procurement', 'procurement_approved'];
const DELIVERY_ALERT_DAYS = 2;

function getDeliveryDaysLeft(deliveryDate) {
  if (!deliveryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const delivery = new Date(deliveryDate);
  delivery.setHours(0, 0, 0, 0);
  return Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));
}

function getDetailAlertMessage(days) {
  if (days < 0) return `Delivery date was ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago. Production must be completed IMMEDIATELY.`;
  if (days === 0) return 'Delivery date is TODAY. Complete production urgently!';
  if (days === 1) return 'Delivery date is TOMORROW. Rush production to meet the deadline.';
  return `Delivery date is in ${days} days. Prioritize and complete production urgently.`;
}

function getDetailAlertColors(days) {
  if (days < 0) return { bg: 'bg-red-50 border-red-400', icon: 'text-red-600', text: 'text-red-800' };
  if (days === 0) return { bg: 'bg-orange-50 border-orange-400', icon: 'text-orange-600', text: 'text-orange-800' };
  return { bg: 'bg-amber-50 border-amber-400', icon: 'text-amber-600', text: 'text-amber-800' };
}

function ProductionDeliveryAlert({ jobOrder, userRole }) {
  if (userRole !== 'production') return null;
  if (!PRODUCTION_ACTIVE_STATUSES.includes(jobOrder.status)) return null;
  const days = getDeliveryDaysLeft(jobOrder.delivery_date || jobOrder.confirmed_delivery_date);
  if (days === null || days > DELIVERY_ALERT_DAYS) return null;

  const message = getDetailAlertMessage(days);
  const colors = getDetailAlertColors(days);
  const heading = days < 0 ? 'OVERDUE' : 'Delivery Due Soon';

  return (
    <Card data-testid="detail-delivery-alert" className={`${colors.bg} border-2 rounded-sm shadow-sm`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Warning size={28} weight="bold" className={`${colors.icon} animate-pulse`} />
          </div>
          <div>
            <p className={`text-base font-bold ${colors.text}`}>
              {heading} - Complete Production Urgently
            </p>
            <p className={`text-sm ${colors.text} mt-1`}>{message}</p>
            <p className={`text-sm font-bold ${colors.text} mt-2`}>
              Delivery Date: {formatDate(jobOrder.delivery_date || jobOrder.confirmed_delivery_date) || jobOrder.delivery_date || jobOrder.confirmed_delivery_date}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DispatchProgressCard({ jobOrder }) {
  const totalWin = jobOrder.no_of_windows || 0;
  const dispatched = jobOrder.windows_dispatched || 0;
  const pending = jobOrder.windows_pending ?? (totalWin - dispatched);
  const pct = totalWin > 0 ? Math.round((dispatched / totalWin) * 100) : 0;

  if (totalWin <= 0) return null;
  const showStatuses = ['scheduled_for_dispatch', 'partially_dispatched', 'dispatched', 'pending_for_installation', 'installation_completed'];
  if (!showStatuses.includes(jobOrder.status)) return null;

  return (
    <Card data-testid="window-tracking-card" className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <CardTitle className="text-xl font-bold text-zinc-950">Dispatch Progress</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-zinc-50 p-4 rounded-sm">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
            <p className="text-3xl font-light text-zinc-950">{totalWin}</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-sm">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Dispatched</p>
            <p className="text-3xl font-light text-orange-600">{dispatched}</p>
          </div>
          <div className="bg-red-50 p-4 rounded-sm">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Pending</p>
            <p className="text-3xl font-light text-red-600">{pending}</p>
          </div>
        </div>
        {jobOrder.dispatch_schedule_date && (
          <div className="mt-4 text-sm text-zinc-600 bg-yellow-50 p-3 rounded-sm">
            <p><strong>Scheduled:</strong> {jobOrder.dispatch_schedule_date} | Driver: {jobOrder.scheduled_driver_name || '--'} | Vehicle: {jobOrder.scheduled_vehicle_number || '--'}</p>
          </div>
        )}
        {dispatched > 0 && (
          <div className="w-full bg-zinc-200 rounded-full h-3 mt-4">
            <div className="bg-orange-500 h-3 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UploadedFilesCard({ jobOrder, canManageAttachments, onUpload, onDeleteAttachment, onViewFile }) {
  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm">
      <CardHeader className="border-b border-zinc-300">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold text-zinc-950">Uploaded Files</CardTitle>
          {canManageAttachments && (
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*,application/pdf" onChange={(e) => onUpload(e, 'job_order')} className="hidden" data-testid="upload-new-job-order" />
                <span className="inline-flex items-center h-9 px-3 text-xs font-bold bg-blue-50 text-blue-700 border border-blue-300 rounded-sm hover:bg-blue-100 transition-colors">
                  + Job Order
                </span>
              </label>
              <label className="cursor-pointer">
                <input type="file" accept="image/*,application/pdf" onChange={(e) => onUpload(e, 'diagram')} className="hidden" data-testid="upload-new-diagram" />
                <span className="inline-flex items-center h-9 px-3 text-xs font-bold bg-blue-50 text-blue-700 border border-blue-300 rounded-sm hover:bg-blue-100 transition-colors">
                  + Diagram
                </span>
              </label>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {(!jobOrder.uploaded_files || jobOrder.uploaded_files.length === 0) ? (
          <p className="text-sm text-zinc-400">No files uploaded</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobOrder.uploaded_files.map((file, idx) => (
              <div key={`${file.filename}-${file.file_type}-${idx}`} className="relative group">
                <FileCard file={file} index={idx} onView={onViewFile} />
                {canManageAttachments && (
                  <button
                    data-testid={`delete-attachment-${idx}`}
                    onClick={(e) => { e.stopPropagation(); onDeleteAttachment(idx, file.filename); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title="Delete attachment"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AssignProductionDialog({ open, onOpenChange, priority, setPriority, onConfirm, loading }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Job Order to Production</DialogTitle>
          <DialogDescription className="sr-only">Set priority and confirm assignment</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Priority (1-10)</Label>
            <Input data-testid="priority-input" type="number" min="1" max="10" value={priority} onChange={(e) => setPriority(e.target.value)} className="h-11" />
            <p className="text-xs text-zinc-500">Higher priority jobs will be shown first in production queue</p>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-assign-button" onClick={onConfirm} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? 'Assigning...' : 'Assign to Production'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UrgentRequestDialog({ open, onOpenChange, jobOrder, form, setForm, onConfirm, loading }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={24} weight="bold" className="text-red-600" /> Request Urgent Priority
          </DialogTitle>
          <DialogDescription className="sr-only">Submit an urgent delivery date request</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
            <p className="text-sm text-amber-800">Current Delivery Date: <span className="font-bold">{formatDate(jobOrder.delivery_date || jobOrder.confirmed_delivery_date) || jobOrder.delivery_date || jobOrder.confirmed_delivery_date}</span></p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Requested Delivery Date *</Label>
            <Input data-testid="urgent-delivery-date" type="date" value={form.requested_delivery_date} onChange={(e) => setForm(prev => ({ ...prev, requested_delivery_date: e.target.value }))} className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Reason for Urgency *</Label>
            <Textarea data-testid="urgent-reason" value={form.reason} onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))} placeholder="Explain why this order needs urgent priority..." className="min-h-24" />
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-urgent-button" onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Submitting...' : 'Submit Urgent Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirmDialog({ open, onOpenChange, jobOrder, isAdvancedStage, onConfirm, loading }) {
  if (!open || !jobOrder) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash size={24} weight="bold" /> Delete Job Order
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm deletion of this job order</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {isAdvancedStage && (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <Warning size={20} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-bold">This Job Order is already in an advanced stage ({jobOrder.status.replace(/_/g, ' ')}). Deletion may affect records.</p>
              </div>
            </div>
          )}
          <p className="text-sm text-zinc-700">Are you sure you want to delete this Job Order? This action cannot be undone.</p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-2">
            {[
              ['Job Order No', jobOrder.jo_number, true],
              ['Customer Name', jobOrder.customer_name],
              ['Delivery Date', formatDate(jobOrder.delivery_date || jobOrder.confirmed_delivery_date) || jobOrder.delivery_date || jobOrder.confirmed_delivery_date]
            ].map(([label, value, bold]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-zinc-500 font-bold">{label}:</span>
                <span className={`text-zinc-950 ${bold ? 'font-bold' : ''}`}>{value}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 font-bold">Current Status:</span>
              <StatusBadge status={jobOrder.status} />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-delete-button" onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Deleting...' : 'Confirm Delete'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function JobOrderDetail() {
  const { jo_number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobOrder, setJobOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showUrgentDialog, setShowUrgentDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [priority, setPriority] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [urgentForm, setUrgentForm] = useState({ requested_delivery_date: '', reason: '' });

  const fetchJobOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/job-orders/${jo_number}`);
      setJobOrder(data);
    } catch {
      toast.error('Failed to fetch job order');
      navigate('/job-orders');
    } finally {
      setLoading(false);
    }
  }, [jo_number, navigate]);

  useEffect(() => {
    fetchJobOrder();
  }, [fetchJobOrder]);

  const handleAssignToProduction = async () => {
    setActionLoading(true);
    try {
      await api.post(
        `/api/job-orders/${jo_number}/assign-production`,
        { assigned_to_production: true, priority: parseInt(priority) }
      );
      toast.success('Job Order assigned to production');
      setShowAssignDialog(false);
      fetchJobOrder();
    } catch {
      toast.error('Failed to assign to production');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUrgentRequest = async () => {
    if (!urgentForm.requested_delivery_date || !urgentForm.reason) {
      toast.error('Please fill all fields');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(
        `/api/job-orders/${jo_number}/urgent-request`,
        {
          jo_number,
          original_delivery_date: jobOrder.delivery_date || '',
          requested_delivery_date: urgentForm.requested_delivery_date,
          reason: urgentForm.reason,
          requested_by: user?.user_id || '',
          status: 'pending'
        }
      );
      toast.success('Urgent request submitted successfully');
      setShowUrgentDialog(false);
      setUrgentForm({ requested_delivery_date: '', reason: '' });
      fetchJobOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit urgent request');
    } finally {
      setActionLoading(false);
    }
  };

  const viewFile = (file) => {
    // New Object Store files: open via authenticated URL
    if (file.storage_path) {
      const fileUrl = getAuthFileUrl(file.storage_path);
      window.open(fileUrl, '_blank');
      return;
    }
    // Legacy base64 files
    const dataUrl = file.data?.startsWith?.('data:') ? file.data : `data:${file.content_type};base64,${file.data}`;
    if (!dataUrl || dataUrl === 'data:undefined;base64,undefined') {
      toast.error('Unable to open attachment — file data is missing');
      return;
    }
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;
    const html = newWindow.document.createElement('html');
    const head = newWindow.document.createElement('head');
    const title = newWindow.document.createElement('title');
    const body = newWindow.document.createElement('body');
    title.textContent = file.filename;
    head.appendChild(title);
    if (file.content_type === 'application/pdf') {
      body.style.margin = '0';
      const embed = newWindow.document.createElement('embed');
      embed.src = dataUrl;
      embed.type = 'application/pdf';
      embed.width = '100%';
      embed.height = '100%';
      body.appendChild(embed);
    } else {
      body.style.cssText = 'margin:0;display:flex;justify-content:center;align-items:center;background:#000;';
      const img = newWindow.document.createElement('img');
      img.src = dataUrl;
      img.style.cssText = 'max-width:100%;max-height:100vh;';
      body.appendChild(img);
    }
    html.appendChild(head);
    html.appendChild(body);
    newWindow.document.documentElement.replaceWith(html);
  };

  const EXEC_EDITABLE_STATUSES = ['pending_procurement', 'procurement_rejected'];
  const isOwnJo = user?.user_id === jobOrder?.created_by;
  const isExecutiveEditable = user?.role === 'executive' && isOwnJo && EXEC_EDITABLE_STATUSES.includes(jobOrder?.status);
  const canEdit = isExecutiveEditable || (user?.role === 'admin');
  const canRequestUrgent = user?.role === 'executive' && !jobOrder?.has_urgent_request && jobOrder?.status !== 'dispatched';
  const canDelete = user?.role === 'admin' || isExecutiveEditable;
  const canManageAttachments = canEdit;
  const isAdvancedStage = jobOrder && ADVANCED_STATUSES.includes(jobOrder.status);
  const isRejected = jobOrder?.status === 'procurement_rejected';

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      if (jo_number.includes('/')) {
        await api.post('/api/job-orders/delete-by-body', { jo_number });
      } else {
        await api.post(`/api/job-orders/${jo_number}/delete`, {});
      }
      toast.success('Job Order deleted successfully');
      navigate('/job-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job order');
    } finally {
      setActionLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteAttachment = async (fileIndex, filename) => {
    if (!window.confirm(`Delete attachment "${filename}"?`)) return;
    try {
      await api.delete(`/api/job-orders/${jo_number}/attachments/${fileIndex}`);
      toast.success(`Attachment "${filename}" deleted`);
      fetchJobOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  const handleUploadFile = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    try {
      await api.post(`/api/job-orders/${jo_number}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully');
      fetchJobOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
    e.target.value = '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading job order...</div>
      </Layout>
    );
  }

  if (!jobOrder) return null;

  const urgentStatus = jobOrder.urgent_request?.status;

  const URGENT_BANNER_CLASSES = {
    pending: 'border-red-400 bg-red-50',
    approved: 'border-green-400 bg-green-50'
  };
  const urgentBannerClass = URGENT_BANNER_CLASSES[urgentStatus] || 'border-zinc-300';
  const urgentIconClass = urgentStatus === 'pending' ? 'text-red-600' : 'text-green-600';

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(-1)} className="h-11">
              <ArrowLeft size={20} weight="bold" className="mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-4xl font-light tracking-tight text-zinc-950">Job Order #{jobOrder.jo_number}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <StatusBadge status={jobOrder.status} />
                {(() => {
                  const src = (jobOrder.source || jobOrder.origin || '').toLowerCase();
                  const isMobile = src === 'mobile';
                  return (
                    <Badge
                      data-testid="jo-detail-origin-badge"
                      className={isMobile
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100 border border-zinc-300'}
                    >
                      {isMobile ? 'Mobile JO' : 'Manual JO'}
                    </Badge>
                  );
                })()}
                {jobOrder.priority > 0 && (
                  <Badge className="bg-red-50 text-red-700 border border-red-200">Priority {jobOrder.priority}</Badge>
                )}
                {jobOrder.has_urgent_request && (
                  <Badge className="bg-red-100 text-red-800 border border-red-300 animate-pulse">Urgent Request Pending</Badge>
                )}
                {urgentStatus === 'approved' && (
                  <Badge className="bg-green-100 text-green-800 border border-green-300">Urgent Approved</Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <Button
                data-testid="edit-job-order-button"
                onClick={() => navigate(`/job-orders/${jo_number}/edit`)}
                variant="outline"
                className="h-11"
              >
                <PencilSimple size={20} weight="bold" className="mr-2" />
                Edit
              </Button>
            )}
            {canRequestUrgent && (
              <Button
                data-testid="urgent-request-button"
                onClick={() => setShowUrgentDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold h-11"
              >
                <Warning size={20} weight="bold" className="mr-2" />
                Request Urgent
              </Button>
            )}
            {user?.role === 'admin' && jobOrder.status === 'procurement_approved' && !jobOrder.assigned_to_production && (
              <Button
                data-testid="assign-production-button"
                onClick={() => setShowAssignDialog(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11"
              >
                <Package size={20} weight="bold" className="mr-2" />
                Assign to Production
              </Button>
            )}
            {canDelete && (
              <Button
                data-testid="delete-job-order-button"
                onClick={() => setShowDeleteDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold h-11"
              >
                <Trash size={20} weight="bold" className="mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Urgent Request Info Banner */}
        {jobOrder.urgent_request && (
          <Card className={`rounded-sm shadow-sm ${urgentBannerClass}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="bold" className={urgentIconClass} />
                <div className="flex-1">
                  <p className="font-bold text-sm">Urgent Request - {urgentStatus.toUpperCase()}</p>
                  <p className="text-sm mt-1">Requested Delivery: <span className="font-bold">{formatDate(jobOrder.urgent_request.requested_delivery_date) || jobOrder.urgent_request.requested_delivery_date}</span></p>
                  <p className="text-sm">Reason: {jobOrder.urgent_request.reason}</p>
                  <p className="text-xs text-zinc-500 mt-1">By: {jobOrder.urgent_request.requested_by} at {formatDate(jobOrder.urgent_request.requested_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Production On Hold Banner */}
        {jobOrder.status === 'production_on_hold' && jobOrder.hold_reason && (
          <Card data-testid="production-on-hold-banner" className="border-2 border-amber-400 bg-amber-50/80 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Warning size={24} weight="bold" className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-amber-800">Production On Hold</p>
                  <p className="text-sm text-amber-700 mt-1"><span className="font-bold">Reason:</span> {jobOrder.hold_reason}</p>
                  {jobOrder.hold_remarks && <p className="text-sm text-amber-600 mt-1"><span className="font-bold">Remarks:</span> {jobOrder.hold_remarks}</p>}
                  {jobOrder.hold_by && <p className="text-xs text-amber-500 mt-1">By: {jobOrder.hold_by} at {formatDate(jobOrder.hold_at)}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Production Delivery Alert */}
        <ProductionDeliveryAlert jobOrder={jobOrder} userRole={user?.role} />

        {/* Procurement Material Tracking (Procurement + Admin only) */}
        <ProcurementTrackingCard
          jobOrder={jobOrder}
          currentUserRole={user?.role}
          onUpdated={fetchJobOrder}
        />

        {/* Revoked by Admin Banner */}
        {jobOrder.status === 'revoked_by_admin' && (
          <Card data-testid="revoked-banner" className="border-2 border-rose-400 bg-rose-50/80 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="bold" className="text-rose-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-rose-800">Revoked by Admin</p>
                  {jobOrder.revoke_reason && <p className="text-sm text-rose-700 mt-1">Reason: {jobOrder.revoke_reason}</p>}
                  {jobOrder.revoked_by && (
                    <p className="text-xs text-rose-600 mt-1">By: {jobOrder.revoked_by} at {formatDate(jobOrder.revoked_at)}</p>
                  )}
                  {jobOrder.previous_status_before_revoke && (
                    <p className="text-xs text-rose-500 mt-1">Previous status: {jobOrder.previous_status_before_revoke.replace(/_/g, ' ')}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin-marked URGENT banner */}
        {jobOrder.is_urgent && (
          <Card data-testid="admin-urgent-banner" className="border-2 border-red-500 bg-red-50/80 rounded-sm shadow-sm urgent-card-pulse">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="bold" className="text-red-600 flex-shrink-0 urgent-blink" />
                <div>
                  <p className="font-bold text-red-800 urgent-blink">URGENT — Marked by Admin</p>
                  {jobOrder.urgent_reason && <p className="text-sm text-red-700 mt-1">Reason: {jobOrder.urgent_reason}</p>}
                  {jobOrder.urgent_marked_by && (
                    <p className="text-xs text-red-600 mt-1">By: {jobOrder.urgent_marked_by} at {formatDate(jobOrder.urgent_marked_at)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rejection Info Banner for Executive */}
        {isRejected && (
          <Card data-testid="rejection-banner" className="border-2 border-red-300 bg-red-50/50 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-red-800">This Job Order was rejected by Procurement</p>
                  {jobOrder.procurement_remarks && (
                    <p className="text-sm text-red-700 mt-1">Reason: {jobOrder.procurement_remarks}</p>
                  )}
                  {isOwnJo && user?.role === 'executive' && (
                    <p className="text-xs text-red-600 mt-2">You can edit and re-submit this Job Order, or delete it.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Job Order Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <JobInfoCard jobOrder={jobOrder} />
          <CustomerCard jobOrder={jobOrder} />
        </div>

        {/* Specifications */}
        <SpecificationsCard jobOrder={jobOrder} />

        {/* Dispatch Info */}
        <DispatchInfoCard jobOrder={jobOrder} />

        {/* Window Dispatch Tracking */}
        <DispatchProgressCard jobOrder={jobOrder} />

        {/* Uploaded Files */}
        <UploadedFilesCard
          jobOrder={jobOrder}
          canManageAttachments={canManageAttachments}
          onUpload={handleUploadFile}
          onDeleteAttachment={handleDeleteAttachment}
          onViewFile={viewFile}
        />
      </div>

      <AssignProductionDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        priority={priority}
        setPriority={setPriority}
        onConfirm={handleAssignToProduction}
        loading={actionLoading}
      />

      <UrgentRequestDialog
        open={showUrgentDialog}
        onOpenChange={setShowUrgentDialog}
        jobOrder={jobOrder}
        form={urgentForm}
        setForm={setUrgentForm}
        onConfirm={handleUrgentRequest}
        loading={actionLoading}
      />

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        jobOrder={jobOrder}
        isAdvancedStage={isAdvancedStage}
        onConfirm={handleDelete}
        loading={actionLoading}
      />
    </Layout>
  );
}
